import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useTranslation } from 'react-i18next';

import Text, { TEXT_COLOR, TEXT_SIZE, TEXT_WEIGHT } from '@components/Text';
import TextArea from '@components/TextArea';
import { Tooltip } from '@components/Tooltip';
import { POPULATION_OPTIONS, TYPE_OPTIONS } from '../tabs/SpeechTopics/config';
import { LOCALE_OPTIONS } from './locale';
import { formatPopulation } from './formatters';
import { arrayShallowEqual } from './collections';
import {
  DetailsActionButton,
  DetailsActions,
  DetailsActionsButtons,
  DetailsEditToolbar,
  DetailsError,
  DetailsField,
  DetailsHeaderRow,
  DetailsInlineRow,
  DetailsPrimaryButton,
  DetailsSelect,
} from './detailsEditorStyles';

const normalizeForm = (item) => ({
  type: item?.type || TYPE_OPTIONS[0]?.value || '',
  locale: item?.locale || LOCALE_OPTIONS[0]?.value || 'EN',
  text: item?.text || '',
  population: Array.isArray(item?.population) && item.population.length
    ? item.population
    : [POPULATION_OPTIONS[0]?.value].filter(Boolean),
});

const StimulusSpeechTopicDetailsEditor = ({ item, onSave, saving }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => normalizeForm(item));

  useEffect(() => {
    if (!isEditing) {
      setForm(normalizeForm(item));
    }
  }, [item, isEditing]);

  const viewPopulationLabel = useMemo(
    () => formatPopulation(item?.population, POPULATION_OPTIONS),
    [item?.population]
  );

  const viewTypeLabel = useMemo(() => {
    if (!item?.type) return '';
    return TYPE_OPTIONS.find((opt) => opt.value === item.type)?.label || item.type;
  }, [item?.type]);

  const hasChanges = useMemo(() => {
    const base = normalizeForm(item);
    return base.type !== form.type
      || base.locale !== form.locale
      || base.text !== form.text
      || !arrayShallowEqual(base.population, form.population);
  }, [form, item]);

  const validationMessage = useMemo(() => {
    if (!form.type) return t('Stimuli.speechTopicsTypeRequired');
    if (!form.text.trim()) return t('Stimuli.textRequired');
    if (!form.population.length) return t('Stimuli.populationRequired');
    return '';
  }, [form.type, form.population.length, form.text, t]);

  const saveTooltipMessage = useMemo(() => {
    if (saving) return '';
    if (validationMessage) return '';
    if (!hasChanges) return t('Stimuli.saveAfterChange');
    return '';
  }, [hasChanges, saving, t, validationMessage]);

  const isSaveDisabled = saving || !hasChanges || !form.type || !form.text.trim() || !form.population.length;
  const detailsDir = form.locale === 'HE' ? 'rtl' : 'ltr';

  const togglePopulation = (value) => {
    setForm((prev) => {
      const has = prev.population.includes(value);
      if (has) {
        return { ...prev, population: prev.population.filter((p) => p !== value) };
      }
      return { ...prev, population: [...prev.population, value] };
    });
  };

  const handleCancel = () => {
    setForm(normalizeForm(item));
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    const saveSucceeded = await onSave({
      type: form.type,
      locale: form.locale,
      text: form.text,
      population: form.population,
    });
    if (saveSucceeded) {
      setIsEditing(false);
    }
  };

  return (
    <>
      {!isEditing ? (
        <>
          <DetailsHeaderRow>
            <Text size={TEXT_SIZE.H3} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.speechTopicDetails')}
            </Text>
            <DetailsEditToolbar>
              <DetailsActionButton type="button" onClick={() => setIsEditing(true)} title={t('Stimuli.editSpeechTopic')}>
                <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {t('Actions.Instance.edit')}
              </DetailsActionButton>
            </DetailsEditToolbar>
          </DetailsHeaderRow>

          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.speechTopicsType')}: {viewTypeLabel}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.locale')}: {item?.locale || ''}
          </Text>
          <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE}>
            {t('Stimuli.population')}: {viewPopulationLabel}
          </Text>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.fullText')}
            </Text>
            <TextArea value={item?.text || ''} readOnly dir={item?.locale === 'HE' ? 'rtl' : 'ltr'} style={{ height: 320, width: '100%' }} />
          </div>
        </>
      ) : (
        <>
          <Text size={TEXT_SIZE.H3} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
            {t('Stimuli.speechTopicDetails')}
          </Text>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.speechTopicsType')}
            </Text>
            <DetailsSelect
              value={form.type}
              onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
              disabled={saving}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </DetailsSelect>
          </DetailsField>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.locale')}
            </Text>
            <DetailsSelect
              value={form.locale}
              onChange={(e) => setForm((prev) => ({ ...prev, locale: e.target.value }))}
              disabled={saving}
            >
              {LOCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </DetailsSelect>
          </DetailsField>

          <DetailsField>
            <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
              {t('Stimuli.population')}
            </Text>
            <DetailsInlineRow>
              {POPULATION_OPTIONS.map((opt) => {
                const checked = form.population.includes(opt.value);
                return (
                  <label key={opt.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={() => togglePopulation(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
              {!form.population.length ? <DetailsError>{t('Stimuli.populationRequired')}</DetailsError> : null}
            </DetailsInlineRow>
          </DetailsField>

          <DetailsField>
            <DetailsInlineRow>
              <Text size={TEXT_SIZE.H4} textColor={TEXT_COLOR.DARK_BLUE} weight={TEXT_WEIGHT.BOLD}>
                {t('Stimuli.fullText')}
              </Text>
              {!form.text.trim() ? <DetailsError>{t('Stimuli.textRequired')}</DetailsError> : null}
            </DetailsInlineRow>
            <TextArea
              value={form.text}
              onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
              dir={detailsDir}
              style={{ height: 320, width: '100%' }}
            />
          </DetailsField>

          <DetailsActions>
            <div>
              {validationMessage ? <DetailsError>{validationMessage}</DetailsError> : null}
            </div>
            <DetailsActionsButtons>
              <DetailsActionButton type="button" onClick={handleCancel} disabled={saving}>
                <CloseOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                {t('Actions.Instance.cancel')}
              </DetailsActionButton>
              {saveTooltipMessage ? (
                <Tooltip content={saveTooltipMessage} placement="top" hasArrow>
                  <span>
                    <DetailsPrimaryButton
                      type="button"
                      onClick={handleSave}
                      disabled={isSaveDisabled}
                    >
                      <SaveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {saving ? t('Stimuli.saving') : t('Actions.Instance.save')}
                    </DetailsPrimaryButton>
                  </span>
                </Tooltip>
              ) : (
                <DetailsPrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  <SaveOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {saving ? t('Stimuli.saving') : t('Actions.Instance.save')}
                </DetailsPrimaryButton>
              )}
            </DetailsActionsButtons>
          </DetailsActions>
        </>
      )}
    </>
  );
};

StimulusSpeechTopicDetailsEditor.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string,
    locale: PropTypes.string,
    text: PropTypes.string,
    population: PropTypes.arrayOf(PropTypes.string),
    wordCount: PropTypes.number,
  }),
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

StimulusSpeechTopicDetailsEditor.defaultProps = {
  item: null,
  saving: false,
};

export default StimulusSpeechTopicDetailsEditor;
